// Pure JWT inspection helpers — no I/O, no Cognito SDK, no browser globals beyond
// an optional `atob`. Kept dependency-free so it runs identically in the browser
// bundle and under `node --test`.
//
// These decide *when* to refresh and whether a token is still usable; the actual
// token storage/refresh lives in auth.js (backed by amazon-cognito-identity-js).

// Seconds before real expiry at which we proactively refresh, so a request never
// races the clock. auth.getValidIdToken() refreshes when needsRefresh() is true.
export const DEFAULT_REFRESH_THRESHOLD_SECONDS = 120;

// Decode a base64url segment to a UTF-8 string. Works in browser (atob) and Node
// (Buffer). Returns null for malformed input.
function base64UrlToString(segment) {
  if (typeof segment !== 'string' || segment.length === 0) return null;
  let b64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const rem = b64.length % 4;
  if (rem === 2) b64 += '==';
  else if (rem === 3) b64 += '=';
  else if (rem === 1) return null; // not a valid base64 length

  try {
    if (typeof atob === 'function') {
      const bin = atob(b64);
      const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
      return new TextDecoder().decode(bytes);
    }
    // Node / test environment.
    return Buffer.from(b64, 'base64').toString('utf8');
  } catch {
    return null;
  }
}

// Decode a JWT's payload (claims) without verifying the signature. Signature
// verification is the API Gateway Cognito authorizer's job; the frontend only
// reads `exp` to schedule refreshes. Returns null if the token is malformed.
export function decodeJwt(token) {
  if (typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  const json = base64UrlToString(parts[1]);
  if (json == null) return null;
  try {
    const claims = JSON.parse(json);
    return claims && typeof claims === 'object' ? claims : null;
  } catch {
    return null;
  }
}

// The `exp` claim (seconds since epoch), or null if absent/invalid.
export function getTokenExpiry(token) {
  const claims = decodeJwt(token);
  if (!claims || typeof claims.exp !== 'number' || !Number.isFinite(claims.exp)) {
    return null;
  }
  return claims.exp;
}

// Seconds until the token expires (negative if already expired), or null if the
// token has no readable expiry.
export function secondsUntilExpiry(token, nowMs = Date.now()) {
  const exp = getTokenExpiry(token);
  if (exp == null) return null;
  return exp - Math.floor(nowMs / 1000);
}

// True if the token is expired (or unreadable — an undecodable token can't be
// trusted). `skewSeconds` treats a token as expired that many seconds early, to
// absorb client/server clock drift.
export function isTokenExpired(token, skewSeconds = 0, nowMs = Date.now()) {
  const exp = getTokenExpiry(token);
  if (exp == null) return true;
  const nowSec = Math.floor(nowMs / 1000);
  return nowSec >= exp - skewSeconds;
}

// True if the token should be refreshed now — either it's within the threshold of
// expiry or it's unreadable. Drives proactive refresh in auth.getValidIdToken().
export function needsRefresh(
  token,
  thresholdSeconds = DEFAULT_REFRESH_THRESHOLD_SECONDS,
  nowMs = Date.now(),
) {
  const remaining = secondsUntilExpiry(token, nowMs);
  if (remaining == null) return true;
  return remaining <= thresholdSeconds;
}
